<script>
import { mapGetters } from 'vuex';

export default {
  computed: {
    ...mapGetters(['getLatestPrescription']),
    
    hasPrescription() {
      return Array.isArray(this.getLatestPrescription) && this.getLatestPrescription.length > 0;
    },

    medicationType() {
      if (!this.hasPrescription) return '';
      
      const firstMed = this.getLatestPrescription.find(med => 
        med.name.toLowerCase().includes('tirzepatide') || 
        med.name.toLowerCase().includes('semaglutide')
      );

      if (!firstMed) return '';
      
      return firstMed.name.toLowerCase().includes('tirzepatide') ? 'Tirzepatide' : 'Semaglutide';
    },

    sortedPrescriptions() {
      if (!this.hasPrescription) return [];

      const filteredPrescriptions = this.getLatestPrescription.filter(med => 
        med.name.toLowerCase().includes(this.medicationType.toLowerCase())
      );

      return filteredPrescriptions.sort((a, b) => {
        const strengthA = this.getDosageFromDirections(a.directions);
        const strengthB = this.getDosageFromDirections(b.directions);
        return strengthA - strengthB;
      });
    },

    prescriptionProgression() {
      if (!this.sortedPrescriptions.length) return [];

      return Array.from({ length: 3 }, (_, index) => {
        const prescriptionIndex = Math.min(index, this.sortedPrescriptions.length - 1);
        const prescription = this.sortedPrescriptions[prescriptionIndex];
        
        return {
          month: index + 1,
          strength: this.getDosageFromDirections(prescription.directions),
          directions: prescription.directions,
          medicationType: this.medicationType
        };
      });
    }
  },

  methods: {
    getDosageFromDirections(directions) {
      const doseMatch = directions.match(/\((\d+\.?\d*)\s*mg dose\)/);
      if (doseMatch) {
        return doseMatch[1];
      }
      return '0';
    },

    getImageUrl(medicationType, strength, isPoster = false) {
      try {
        const suffix = isPoster ? '-p-500' : '';
        return require(`@/assets/images/${medicationType}-${strength}mg${suffix}.png`);
      } catch (e) {
        console.error('Error loading image:', e);
        return ''; // Return empty string if image not found
      }
    }
  }
}
</script>

<template>
  <div>
    <div v-if="hasPrescription" class="dosage-tab-div">
      <div v-for="monthData in prescriptionProgression" :key="monthData.month" class="dosage-month-div">
        <div class="text-block-1256">Month {{ monthData.month }}</div>
        <div class="dosage-flex-div">
          <div class="div-block-1986">
            <div class="step-header">
              <span class="text-span-275">{{ monthData.strength }}mg</span> Once Weekly Shot.
            </div>
            <img :sizes="'(max-width: 479px) 94vw, (max-width: 767px) 32vw, (max-width: 991px) 232.953125px, 300.796875px'" 
                 :srcset="`${getImageUrl(monthData.medicationType, monthData.strength, false)} 500w, ${getImageUrl(monthData.medicationType, monthData.strength)} 525w`" 
                 :src="getImageUrl(monthData.medicationType, monthData.strength)"
                 loading="lazy" 
                 class="image-547">
            <!-- Rest of the template remains the same -->
            <div class="text-block-1258">Supply List:</div>
            <ul role="list" class="list-12">
              <li class="list-item-10">
                <div class="text-block-1259"><span class="supply-list-span">1</span> Vial of 2ml Bacteriostatic Water</div>
              </li>
              <li class="list-item-10">
                <div class="text-block-1259"><span class="supply-list-span">1</span> Vial of {{ monthData.strength }}MG {{ monthData.medicationType }}</div>
              </li>
              <li class="list-item-10">
                <div class="text-block-1259"><span class="supply-list-span">1</span> 3ml Wide Gauge Syringe &amp; Needle</div>
              </li>
              <li class="list-item-10">
                <div class="text-block-1259"><span class="supply-list-span">4</span> Subcutaneous Needles for Weekly Injections.</div>
              </li>
            </ul>
          </div>
          <div class="dosage-steps-div">
            <div class="dosage-step-1-2-div">
              <div class="dosage-step-div">
                <div class="step-header"><span class="text-span-275">Step 1:</span> Reconstitute Medication</div>
                <p class="paragraph-161">Take the Bacteriostatic water with larger gauge needle and syringe. Add 2ml of Bacteriostatic Water to the the <span class="text-span-276">{{ monthData.strength }}mg peptide vial.</span></p>
                <img loading="lazy" src="@/assets/images/image_SyringeLarge.svg" alt="" class="image-548">
                <a href="http://www.prioritymeds.com/help-desk/how-to-reconstitute-your-peptides" class="button-124 w-button" target="_blank">Watch Tutorial Video</a>
              </div>
              <div class="dosage-step-div">
                <div class="step-header"><span class="text-span-275">Step 2:</span> Weekly Injection</div>
                <p class="paragraph-161">{{ monthData.directions }}</p>
                <img loading="lazy" src="@/assets/images/image_Syringe.svg" alt="" class="image-548">
                <a href="http://www.prioritymeds.com/help-desk/how-to-inject-your-peptides" class="button-124 w-button" target="_blank">Watch Tutorial Video</a>
              </div>
            </div>
            <div class="dosage-step-3-div">
                        <div class="dosage-step-div step-3">
                          <div class="step-header step-3"><span class="text-span-275">Step 3:</span> B12 Injection</div>
                          <div class="text-block-1260">1000mgc/ml Once Weekly*.</div>
                          <p class="paragraph-161">Inject 100 Units (1ml) with Subcutaneous syringe once weekly.</p><img loading="lazy" src="@/assets/images/image_Syringe_B12.svg" alt="" class="image-548">
                        </div>
                        <div class="dosage-step-div"><img sizes="(max-width: 479px) 94vw, (max-width: 767px) 30vw, (max-width: 991px) 218.9765625px, 282.7421875px" srcset="@/assets/images/Cyanocobalamin-p-500.png 500w, @/assets/images/Cyanocobalamin.png 525w" alt="" src="@/assets/images/Cyanocobalamin.png" loading="lazy" class="image-549">
                          <div class="div-block-1987">
                            <div class="text-block-1261">This medication does not need to be reconstituted. This vial will last for 3 months</div>
                          </div>
                        </div>
                      </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      No prescription
    </div>
  </div>
</template>